import { ExploreWidgetConfig } from "../../config"

const OSM_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors'
// Mapbox ToS requires Mapbox + OSM attribution and an improve-this-map link on
// pages using Mapbox tiles.
const MAPBOX_ATTRIBUTION = '&copy; <a href="https://www.mapbox.com/about/maps/" target="_blank" rel="noopener">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> <a href="https://apps.mapbox.com/feedback/" target="_blank" rel="noopener">Improve this map</a>'

const MAPBOX_TOKEN_RE = /^pk\.[A-Za-z0-9._-]{8,200}$/
const MAPBOX_STYLE_RE = /^[\w-]+\/[\w.-]+$/
const DEFAULT_MAPBOX_STYLE = 'mapbox/streets-v12'

export type LeafletTileConfig = {
  url: string
  attribution: string
  provider: 'osm' | 'mapbox'
}

/**
 * Resolve the Leaflet tile layer for the configured provider (#852). Falls back
 * to OSM whenever Mapbox is requested without a usable public token/style, or
 * after a tile-layer error (`tileErrorFallback` — revoked tokens and missing
 * styles only fail asynchronously as 401/404 tile responses). The URL never
 * string-interpolates user input: style segments are validated then encoded,
 * and the token rides URLSearchParams. Values are type-checked, not just
 * regex-tested: the property-map path (#1184) passes RAW feed JSON with no
 * zod parse, and `RegExp.test` coerces — `["owner/style"]` would pass the
 * pattern and then crash on the array's missing `.split`.
 */
export function resolveLeafletTileConfig(
  map: Partial<ExploreWidgetConfig['map']> | undefined,
  tileErrorFallback = false,
): LeafletTileConfig {
  const osm: LeafletTileConfig = { url: OSM_URL, attribution: OSM_ATTRIBUTION, provider: 'osm' }
  if (tileErrorFallback || !map || map.tileProvider !== 'mapbox') return osm

  const token = map.mapboxPublicToken
  if (typeof token !== 'string' || !MAPBOX_TOKEN_RE.test(token)) return osm

  const style = typeof map.mapboxStyle === 'string' && MAPBOX_STYLE_RE.test(map.mapboxStyle) ? map.mapboxStyle : DEFAULT_MAPBOX_STYLE
  const [owner, styleId] = style.split('/')
  const params = new URLSearchParams({ access_token: token })
  return {
    url: `https://api.mapbox.com/styles/v1/${encodeURIComponent(owner)}/${encodeURIComponent(styleId)}/tiles/256/{z}/{x}/{y}@2x?${params.toString()}`,
    attribution: MAPBOX_ATTRIBUTION,
    provider: 'mapbox',
  }
}
